import { Fragment, ReactNode } from 'react';
import { Box, Divider, Stack, Typography } from '@mui/material';
import { InfoCircleOutlined } from '@ant-design/icons';
import { alpha, Theme } from '@mui/material/styles';
import { DISPLAY_FONT, RADIUS, REDUCED_MOTION } from 'components/ui/tokens';
import { OfferCardView } from '../offers';
import { Callout } from './StepParts';

// The price the offer replaces is struck as the step settles, just after its icon pops: the line draws
// left to right while the price greys out.
const STRIKE_MOTION = '.5s cubic-bezier(.2,.7,.3,1) .45s backwards';
const strikeSx = (theme: Theme) => ({
  position: 'relative' as const,
  display: 'inline-block',
  color: theme.palette.text.secondary,
  animation: `offerStrikeFade ${STRIKE_MOTION}`,
  '@keyframes offerStrikeFade': { from: { color: theme.palette.text.primary } },
  '@keyframes offerStrike': { from: { transform: 'scaleX(0)' } },
  '&::after': {
    content: '""',
    position: 'absolute' as const,
    left: -2,
    right: -2,
    top: '52%',
    height: '1.5px',
    borderRadius: 1,
    bgcolor: 'currentColor',
    transformOrigin: 'left center',
    animation: `offerStrike ${STRIKE_MOTION}`
  },
  [REDUCED_MOTION]: { animation: 'none', '&::after': { animation: 'none' } }
});

/**
 * An offer at a glance: the headline, its pill and the figures that make it up; `children` (what it covers)
 * sits under the card, and the note on how it is used closes it.
 */
export default function OfferCard({ headline, pill, sections, note, children }: OfferCardView & { children?: ReactNode }) {
  return (
    <Stack spacing={1.5}>
      <Box
        sx={(theme) => ({
          p: 2.25,
          textAlign: 'left',
          borderRadius: RADIUS.card,
          border: `1px solid ${alpha(theme.palette.primary.main, 0.28)}`,
          bgcolor: alpha(theme.palette.primary.main, 0.06)
        })}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1.5}>
          <Typography sx={{ fontFamily: DISPLAY_FONT, fontSize: 32, fontWeight: 600, lineHeight: 1.1, color: 'primary.main' }}>
            {headline}
          </Typography>
          <Box
            component="span"
            sx={{
              flexShrink: 0,
              px: 1.25,
              py: 0.5,
              borderRadius: RADIUS.pill,
              fontSize: 12,
              fontWeight: 600,
              color: 'primary.contrastText',
              bgcolor: 'primary.main'
            }}
          >
            {pill}
          </Box>
        </Stack>
        <Stack spacing={1.5} sx={{ mt: 2 }}>
          {sections.map((section, index) => (
            <Fragment key={section.title || index}>
              {index > 0 && <Divider />}
              <Box>
                {section.title && (
                  <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 600 }}>
                    {section.title}
                  </Typography>
                )}
                <Stack component="dl" spacing={0.75} sx={{ m: 0 }}>
                  {section.rows.map(({ label, value, struck }) => (
                    <Stack key={label} direction="row" justifyContent="space-between" spacing={2}>
                      <Typography component="dt" variant="body2" color="text.secondary">
                        {label}
                      </Typography>
                      <Typography component="dd" variant="body2" sx={{ m: 0, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                        {struck ? (
                          <Box component="span" sx={strikeSx}>
                            {value}
                          </Box>
                        ) : (
                          value
                        )}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              </Box>
            </Fragment>
          ))}
        </Stack>
      </Box>
      {children}
      <Callout icon={<InfoCircleOutlined />} tone="primary" title={note.title}>
        <Typography variant="body2" color="text.secondary">
          {note.body}
        </Typography>
      </Callout>
    </Stack>
  );
}
